import { ROUTES } from "@template/types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Outlet, useLocation } from "react-router-dom";
import { useFinance } from "./FinanceContext";
import { CURRENCIES, SCENARIOS, SEED } from "./data/seedData";
import type { CurrencyCode, Scenario } from "./data/types";
import { VoucherModal } from "./components/VoucherModal";
import { FrozenEditModal } from "./components/FrozenEditModal";

/** Finance-portal chrome shared by every finance page: currency/scenario controls, pending-change banners, modals. */
export function FinanceLayout() {
  const { cur, setCur, scenario, setScenario, saved, staged, versions, setPage } = useFinance();
  const location = useLocation();

  const revisionsPending = versions.filter((v) => v.status === "Revision").length;
  const onApprovalsOrUpload =
    location.pathname === ROUTES.financeApprovals ||
    location.pathname === ROUTES.financeTallyImport;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {SEED.company} · {SEED.fiscalYear}
          </Typography>
        </Box>
        <Fade in={saved}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "success.main" }}>
            <CheckCircleIcon fontSize="small" />
            <Typography variant="body2">Saved</Typography>
          </Stack>
        </Fade>
        <TextField
          select
          size="small"
          label="Scenario"
          value={scenario}
          onChange={(e) => setScenario(e.target.value as Scenario)}
          sx={{ minWidth: 160 }}
        >
          {Object.keys(SCENARIOS).map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Currency"
          value={cur}
          onChange={(e) => setCur(e.target.value as CurrencyCode)}
          sx={{ minWidth: 130 }}
        >
          {Object.keys(CURRENCIES).map((k) => (
            <MenuItem key={k} value={k}>
              {CURRENCIES[k as CurrencyCode].symbol} {k}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {staged && staged.status !== "Published" && !onApprovalsOrUpload ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClick={() => setPage("approvals")}
          style={{ cursor: "pointer" }}
        >
          Version <strong>{staged.ver}</strong> ({staged.month}) staged · {staged.diff.counts.total}{" "}
          pending changes · status <strong>{staged.status}</strong> · click to review
        </Alert>
      ) : null}
      {revisionsPending > 0 && !onApprovalsOrUpload ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClick={() => setPage("approvals")}
          style={{ cursor: "pointer" }}
        >
          {revisionsPending} frozen-period revision(s) awaiting dual approval · click to review
        </Alert>
      ) : null}

      <Outlet />

      <VoucherModal />
      <FrozenEditModal />
    </Box>
  );
}
